import {
  type ChangeEvent,
  type KeyboardEvent,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { Dropdown } from '../Dropdown/Dropdown';
import type { DropdownItemProps } from '../Dropdown/DropdownItem';
import { InputChevron } from '../Input/InputChevron';
import { Search, type SearchFill, type SearchState } from '../Search/Search';
import { useControllableState } from '../../hooks/useControllableState';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useAnchoredPanelPosition } from '../../hooks/useAnchoredPanelPosition';
import { useListNavigation } from '../../hooks/useListNavigation';
import { getOverlayPortalParent } from '../../utils/getOverlayPortalParent';
import './AutoComplete.css';

const DROPDOWN_ITEM_ID_PREFIX = 'spark-dropdown-item-';

export type AutoCompleteSize = 'small' | 'medium';
export type AutoCompleteType = 'default' | 'multi-select';
export type AutoCompletePanelLayout = 'overlay' | 'inline';

/** Search input with a dropdown suggestions panel. Supports single-select and multi-select (tag) modes. */
export type AutoCompleteProps = {
  /** Controlled open state of the suggestions panel. */
  open?: boolean;
  /** Initial open state for uncontrolled mode. */
  defaultOpen?: boolean;
  /** Fired when the panel opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /**
   * `'overlay'` (default) portals the panel to `document.body` with fixed
   * positioning so it escapes ancestor `overflow` clipping (e.g. modals).
   * `'inline'` keeps the panel in normal flow (docs/matrix stories).
   */
  panelLayout?: AutoCompletePanelLayout;
  /** Input and panel sizing. */
  size?: AutoCompleteSize;
  /** `'default'` for single value, `'multi-select'` for tag-based selection. */
  type?: AutoCompleteType;
  placeholder?: string;
  value?: string;
  fill?: SearchFill;
  visualState?: SearchState;
  tags?: string[];
  overflowCount?: number;
  showFooter?: boolean;
  disabled?: boolean;
  items?: DropdownItemProps[];
  /** Message shown in the panel when there are no matching items. */
  emptyText?: string;
  /** Fired when the search text changes. */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Fired when a dropdown item is selected. */
  onSelect?: (item: DropdownItemProps, index: number) => void;
  /** Fired when a selected tag is removed (multi-select). */
  onTagRemove?: (tag: string, index: number) => void;
  /**
   * Select-style trigger: hide the search glyph and show InputChevron
   * (up/down with open state) on the trailing edge.
   */
  showChevron?: boolean;
  /** Ref to the underlying search `<input>` (e.g. for programmatic focus). */
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
};

export function AutoComplete({
  open,
  defaultOpen = false,
  onOpenChange,
  panelLayout = 'overlay',
  size = 'medium',
  type = 'default',
  placeholder = 'Search',
  value,
  fill,
  visualState,
  tags = [],
  overflowCount = 0,
  showFooter = false,
  disabled = false,
  items = [],
  emptyText = 'No results',
  onChange,
  onSelect,
  onTagRemove,
  showChevron = false,
  inputRef,
  className = '',
}: AutoCompleteProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useControllableState(open, defaultOpen, onOpenChange);

  const close = useCallback(() => setOpen(false), [setOpen]);

  const isPortaled = panelLayout === 'overlay';

  // The portaled panel lives outside rootRef in the DOM, so it must be treated
  // as "inside" for outside-click detection to avoid closing before selection.
  useOutsideClick([rootRef, panelRef], close, isOpen);
  useEscapeKey(close, isOpen);

  const isMultiSelect = type === 'multi-select';
  const hasContent = isMultiSelect ? tags.length > 0 : value !== undefined && value.length > 0;
  const resolvedFill: SearchFill = fill ?? (hasContent ? 'filled' : 'empty');
  // Multi-select opens as soon as the field is focused so the first option can be
  // picked with no tags yet; single-select keeps the "filled" suggestion behaviour.
  const showPanel = isOpen && !disabled && (isMultiSelect || resolvedFill === 'filled');

  const panelStyle = useAnchoredPanelPosition(rootRef, panelRef, showPanel && isPortaled);
  const resolvedVisualState: SearchState = disabled
    ? 'disabled'
    : showPanel
      ? 'click-on'
      : visualState ?? 'default';

  const filteredItems = useMemo(() => {
    if (!value || value.length === 0) return items;
    const query = value.toLowerCase();
    return items.filter((item) => (item.label ?? '').toLowerCase().includes(query));
  }, [items, value]);

  // Keyboard highlight lives here (not on Dropdown) because focus stays on the
  // Search input while the suggestions panel is open.
  const { activeIndex, setActiveIndex, handleKeyDown: handleListKeyDown } = useListNavigation({
    itemCount: filteredItems.length,
  });

  // Callers often pass a fresh `items` array each render; key off labels so the
  // highlight only resets when the visible options actually change.
  const filteredItemKey = useMemo(
    () => filteredItems.map((item) => item.label ?? '').join('\0'),
    [filteredItems],
  );

  useEffect(() => {
    setActiveIndex(-1);
  }, [filteredItemKey, setActiveIndex]);

  useEffect(() => {
    if (!showPanel) {
      setActiveIndex(-1);
    }
  }, [showPanel, setActiveIndex]);

  useEffect(() => {
    if (!showPanel || activeIndex < 0) return;
    const activeEl = panelRef.current?.querySelector<HTMLElement>(
      `#${DROPDOWN_ITEM_ID_PREFIX}${activeIndex}`,
    );
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, showPanel]);

  const highlightedItems = useMemo(
    () =>
      filteredItems.map((item, index) =>
        index === activeIndex && !item.visualState
          ? { ...item, visualState: 'hover' as const }
          : item,
      ),
    [filteredItems, activeIndex],
  );

  const clearMultiSelectQuery = useCallback(() => {
    if (!isMultiSelect || !value) return;
    onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
  }, [isMultiSelect, value, onChange]);

  const selectItemAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= filteredItems.length) return;
      const item = filteredItems[index];
      if (item.state === 'disabled' || item.visualState === 'disabled') return;
      item.onClick?.();
      onSelect?.(item, index);
      if (isMultiSelect) {
        clearMultiSelectQuery();
      } else {
        setOpen(false);
      }
    },
    [filteredItems, onSelect, isMultiSelect, setOpen, clearMultiSelectQuery],
  );

  const handleDropdownSelect = useCallback(
    (item: DropdownItemProps, index: number) => {
      onSelect?.(item, index);
      clearMultiSelectQuery();
    },
    [onSelect, clearMultiSelectQuery],
  );

  const rootClasses = [
    'spark-auto-complete',
    `spark-auto-complete-${size}`,
    showPanel ? 'spark-auto-complete-open' : 'spark-auto-complete-closed',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    if (event.target.value.length > 0 && !isOpen) {
      setOpen(true);
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!isOpen && (isMultiSelect || resolvedFill === 'filled')) {
        setOpen(true);
      }
      if (filteredItems.length > 0) {
        handleListKeyDown(event);
      }
      return;
    }

    if (event.key === 'Enter' && showPanel && filteredItems.length > 0) {
      const indexToSelect = activeIndex >= 0 ? activeIndex : 0;
      event.preventDefault();
      selectItemAt(indexToSelect);
    }
  };

  return (
    <div className={rootClasses} ref={rootRef}>
      <Search
        ref={inputRef}
        size={size}
        placeholder={placeholder}
        value={value}
        multiSelect={isMultiSelect}
        tags={tags}
        overflowCount={overflowCount}
        disabled={disabled}
        fill={resolvedFill}
        visualState={resolvedVisualState}
        showFocus={showPanel}
        showSearchIcon={!showChevron}
        endIcon={showChevron ? <InputChevron open={showPanel} disabled={disabled} /> : undefined}
        onChange={handleChange}
        onKeyDown={handleSearchKeyDown}
        onTagRemove={onTagRemove}
        role="combobox"
        aria-expanded={showPanel}
        aria-autocomplete="list"
        aria-activedescendant={
          showPanel && activeIndex >= 0 ? `${DROPDOWN_ITEM_ID_PREFIX}${activeIndex}` : undefined
        }
      />
      {showPanel ? (() => {
        const panel = (
          <div
            ref={panelRef}
            className={`spark-auto-complete-panel${isPortaled ? ' spark-auto-complete-panel--portal' : ''}`}
            style={isPortaled ? panelStyle : undefined}
          >
            {highlightedItems.length > 0 ? (
              <Dropdown
                type={isMultiSelect ? 'checkbox' : 'regular'}
                showFooter={showFooter}
                items={highlightedItems}
                onSelect={handleDropdownSelect}
              />
            ) : (
              <div className="spark-auto-complete-empty" role="status">
                {emptyText}
              </div>
            )}
          </div>
        );
        return isPortaled ? createPortal(panel, getOverlayPortalParent(rootRef.current)) : panel;
      })() : null}
    </div>
  );
}
